import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, BookOpen, Clock3, Library, PenLine, RadioTower, X } from "lucide-react";
import { useId } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "../lib/useDialogFocus";
import type { Topic, UniverseStoryMatch, UniverseStoryReference } from "../types";

export function StoryVersionChoice({ topic, match, onClose, onRead, onWrite }: {
  topic: Pick<Topic, "title">; match: UniverseStoryMatch;
  onClose: () => void; onRead: (story: UniverseStoryReference) => void; onWrite: () => void;
}) {
  const reduced = useReducedMotion();
  const ref = useDialogFocus<HTMLElement>(true, onClose);
  const id = useId();
  const options = [match.library, match.broadcast].filter(Boolean) as UniverseStoryReference[];
  return createPortal(<motion.div className="version-picker-backdrop"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: reduced ? 0 : .18 }}
    onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <motion.section ref={ref} className="version-picker" role="dialog" aria-modal="true"
      aria-labelledby={id + "-title"} aria-describedby={id + "-topic"} tabIndex={-1}
      initial={reduced ? false : { opacity: 0, y: 12, scale: .99 }}
      animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: reduced ? 0 : 8 }}
      transition={{ duration: reduced ? 0 : .22, ease: [.22, 1, .36, 1] }}>
      <header className="version-picker-heading">
        <span className="version-picker-symbol" aria-hidden="true"><BookOpen size={20} /></span>
        <button type="button" className="version-picker-close" onClick={onClose} aria-label="Закрыть выбор"><X size={19} /></button>
        <h2 id={id + "-title"}>Прочитать или создать?</h2>
        <p>Можно открыть готовую историю или написать свою версию.</p>
      </header>
      <div className="version-picker-topic" id={id + "-topic"}><span>Выбранная тема</span><p>{topic.title}</p></div>
      <div className="version-picker-options" aria-label="Готовые истории">{options.map(story => <button
        type="button" className="version-picker-story" key={story.source + "-" + story.id}
        onClick={() => onRead(story)} aria-label={"Читать: " + story.title + (story.source === "library" ? " — из вашей библиотеки" : " — опубликованная история")}>
        <span className="version-picker-source">{story.source === "library" ? <><Library size={14} />В вашей библиотеке</> : <><RadioTower size={14} />Опубликовано{story.author ? " · " + story.author.displayName : ""}</>}</span>
        <strong className="version-picker-story-title">{story.title}</strong>
        {story.excerpt && <span className="version-picker-excerpt">{story.excerpt}</span>}
        <span className="version-picker-story-footer"><span className="version-picker-meta"><Clock3 size={14} />{story.readTime} мин<span aria-hidden="true">·</span>{story.era}</span><span className="version-picker-read">Читать<ArrowUpRight size={16} /></span></span>
      </button>)}</div>
      <footer className="version-picker-footer">
        <button type="button" className="version-picker-create" onClick={onWrite}><PenLine size={18} /><span>Создать свою версию</span><ArrowUpRight size={17} /></button>
        <p>Тема откроется в студии. Генерация пока не начнётся.</p>
      </footer>
    </motion.section>
  </motion.div>, document.body);
}
